import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './HomePage.css';
import { useGenies } from './GenieContext';
import api from '../api';

const baseSolutions = [
  {
    name: 'Ecolane & TripMaster',
    title: 'IVA Trip Management',
    status: 'Active',
    link: '/learn-more/ecolane-iva-trip-management',
    buttonText: 'Learn More'
  },
  {
    name: 'AI Studio',
    title: '',
    status: 'Active',
    link: '/genies',
    buttonText: 'Launch'
  },
  // Power BI will be conditionally added here
  {
    name: 'FASTER',
    title: 'IVA Service Requests',
    status: 'Coming Soon',
    link: '/learn-more/iva-service-requests',
    buttonText: 'Learn More'
  },
  {
    name: 'FASTER',
    title: 'AI Technician App',
    status: 'Coming Soon',
    link: '/learn-more/ai-technician-app',
    buttonText: 'Learn More'
  },
  {
    name: 'Eligibility',
    title: '',
    status: 'Coming Soon',
    link: '/learn-more/eligibility',
    buttonText: 'Learn More'
  }
];

function HomePage() {
  const context = useGenies();
  const selectedClient = context?.selectedClient || null;
  const [hasPowerBIAccess, setHasPowerBIAccess] = useState(false);
  const [powerBIDashboards, setPowerBIDashboards] = useState([]);
  const [allDashboards, setAllDashboards] = useState([]);
  
  // Fetch all available dashboards for name lookup (non-admin endpoint — accessible to viewer role)
  useEffect(() => {
    const fetchAllDashboards = async () => {
      try {
        const response = await api.get('/api/powerbi/dashboards');
        // GET /api/powerbi/dashboards returns { success, dashboards: [{id, name, description}] }
        setAllDashboards((response.data && response.data.dashboards) ? response.data.dashboards : []);
      } catch (error) {
        console.error('[HomePage] Error fetching all dashboards:', error);
      }
    };
    
    fetchAllDashboards();
  }, []);
  
  // Check Power BI access and fetch assigned dashboards
  useEffect(() => {
    const checkPowerBIAccess = async () => {
      // If no client is selected, reset access state
      if (!selectedClient) {
        setHasPowerBIAccess(false);
        setPowerBIDashboards([]);
        return;
      }
      
      try {
        // Use the non-admin endpoint so viewers can also check PowerBI access
        const response = await api.get(`/api/powerbi/client-access?client=${encodeURIComponent(selectedClient)}`);
        setHasPowerBIAccess(response.data.hasPowerBIAccess || false);
        console.log('[HomePage] Power BI access for client', selectedClient, ':', response.data.hasPowerBIAccess);
        
        if (response.data.hasPowerBIAccess) {
          setPowerBIDashboards(response.data.dashboardIds || []);
          console.log('[HomePage] Client dashboards:', response.data.dashboardIds);
        } else {
          setPowerBIDashboards([]);
        }
      } catch (error) {
        console.error('[HomePage] Error checking Power BI access:', error);
        setHasPowerBIAccess(false);
        setPowerBIDashboards([]);
      }
    };

    checkPowerBIAccess();
  }, [selectedClient]);
  
  // Determine Power BI name, title, and link based on number of dashboards
  const getPowerBIConfig = () => {
    if (powerBIDashboards.length === 0) {
      return { name: 'Power BI', title: 'Dashboard', link: '/powerbi-dashboard' };
    } else if (powerBIDashboards.length === 1) {
      // Single dashboard - link directly to Power BI dashboard with the specific id & name
      const dashboardId = powerBIDashboards[0];
      const dashboard = allDashboards.find(d => d.id === dashboardId);
      const dashboardName = dashboard ? dashboard.name : 'Power BI';
      return {
        name: dashboardName,
        title: '',
        link: `/powerbi-dashboard?id=${encodeURIComponent(dashboardId)}&name=${encodeURIComponent(dashboardName)}`
      };
    } else {
      // Multiple dashboards - link to insights page
      return { name: 'Data Studio', title: '', link: '/insights' };
    }
  };
  
  // Create Power BI solution object with dynamic name/title/link
  const powerBIConfig = getPowerBIConfig();
  const powerBISolution = {
    name: powerBIConfig.name,
    title: powerBIConfig.title,
    status: 'Active',
    link: powerBIConfig.link,
    buttonText: 'Launch'
  };
  
  // Build solutions array with conditional Power BI
  // Only show Power BI tile if user has access AND has at least one dashboard assigned
  const solutions = [...baseSolutions];
  if (hasPowerBIAccess && powerBIDashboards.length > 0) {
    solutions.splice(2, 0, powerBISolution); // Insert Power BI at position 2 (after Data Analytics Studio)
  }
  
  // Split tiles into rows of 3:
  //   5 tiles → row1: 3 small, row2: 2 small (left-aligned)
  //   6 tiles → row1: 3 small, row2: 3 small
  const firstRowCount = 3;
  const firstRowSolutions = solutions.slice(0, firstRowCount);
  const secondRowSolutions = solutions.slice(firstRowCount);

  const renderSolutionCard = (solution, index, cardSize) => (
    <div key={index} className={`solution-card ${cardSize}`} data-status={solution.status}>
      <div className="card-top-content">
        <div className="solution-status">{solution.status}</div>
        {/* title = bold main heading; name = muted subname.
            If title is empty (no subtitle pair), fall back to showing name as the bold heading. */}
        <div className="solution-name">{solution.title || solution.name}</div>
        {solution.title && solution.name && <div className="solution-title">{solution.name}</div>}
      </div>
      <Link
        to={solution.link}
        className="solution-button"
      >
        {solution.buttonText}
      </Link>
    </div>
  );

  return (
    <>
      <div className="home-background" />
      <div className="homepage-content">
        <h1 className="welcome-text">Welcome to Transit Technologies AI Suite</h1>
        <div className="solutions-layout">
          <div className="solutions-row-1">
            {firstRowSolutions.map((solution, index) => renderSolutionCard(solution, index, 'card-sm'))}
          </div>
          <div className="solutions-row-2">
            {secondRowSolutions.map((solution, index) => renderSolutionCard(solution, index + 3, 'card-sm'))}
          </div>
        </div>
      </div>
    </>
  );
}

export default HomePage;
